import { Alert, Button, Divider, Select } from "antd";
import Title from "antd/es/typography/Title";
import { useFormikContext } from "formik";
import { ExecuteReportValues, AdaptedValues } from "shared/types";
import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { adaptMaterials, adaptPackagesForExecuteReport } from "shared/adapters";
import {
  PackagingItem,
  useGetAllMaterialsNoPaginationQuery,
  useGetAllPackagesQuery,
} from "shared/api";
import { Colors } from "shared/themes";
import { FormSelect, Loader } from "shared/ui";
import { selectFiltering } from "shared/helpers";

interface MultiplePackagingFilterProps {
  adaptedOrders: AdaptedValues[];
  selectedOrderId: number | null;
  onOrderChange: (value: number) => void;
  isLoadingOrders: boolean;
}

const MultiplePackagingFilter: FC<MultiplePackagingFilterProps> = ({
  adaptedOrders,
  selectedOrderId,
  onOrderChange,
  isLoadingOrders,
}) => {
  const { t } = useTranslation("", { keyPrefix: "executeReports" });

  const { values, setFieldValue, handleBlur } =
    useFormikContext<ExecuteReportValues>();

  const expandOnOrderChange = (value: number) => {
    onOrderChange(value);
    setFieldValue("order_id", value);
  };

  const {
    data: materials,
    isLoading,
    isFetching,
  } = useGetAllMaterialsNoPaginationQuery(
    {
      order: selectedOrderId ?? undefined,
    },
    {
      skip: !selectedOrderId,
    }
  );

  const adaptedMaterials = useMemo(() => {
    return adaptMaterials(materials ?? []);
  }, [materials]);

  const { data: packagings, isLoading: isLoadingPackaging } =
    useGetAllPackagesQuery(
      {
        order: selectedOrderId ?? undefined,
        material: values?.material_id ?? undefined,
      },
      {
        skip: !selectedOrderId || !values?.material_id,
      }
    );

  const adaptedPackages = useMemo(() => {
    return adaptPackagesForExecuteReport(
      packagings as unknown as PackagingItem[]
    );
  }, [packagings]);

  const selectAllPackages = () => {
    const allPackages = adaptedPackages.map((pack) => pack.value);
    setFieldValue("packaging_ids", allPackages);
  };

  if (isLoading || isFetching || isLoadingPackaging) {
    return <Loader type="fullscreen-backdrop" />;
  }

  return (
    <>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />

      <div className="flex gap-x-3">
        <div className="flex gap-x-2 items-center">
          <Title level={5} className="!w-[80px] !m-0">
            {t("selectOrder")}
          </Title>
          {" - "}
          <Select
            loading={isLoadingOrders}
            value={selectedOrderId}
            placeholder={t("selectOrder")}
            options={adaptedOrders}
            onChange={expandOnOrderChange}
            className="w-full"
            size="small"
            showSearch
            filterOption={(input, option) => selectFiltering(input, option)}
          />
        </div>

        {selectedOrderId && (
          <>
            <Divider
              type="vertical"
              className="h-full"
              style={{ background: Colors.blue.primary }}
            />

            <div className="flex gap-x-2 items-center">
              <Title level={5} className="!w-[110px]  !m-0">
                {t("selectMaterial")}
              </Title>
              {" - "}
              <FormSelect
                value={values?.material_id}
                placeholder={t("selectMaterial")}
                fieldName="material_id"
                options={adaptedMaterials}
                setFieldValue={setFieldValue}
                handleBlur={handleBlur}
                showSearch
                size="small"
                className="!m-0"
              />
            </div>
          </>
        )}

        {values?.material_id && (
          <>
            <Divider
              type="vertical"
              className="h-full"
              style={{ background: Colors.blue.primary }}
            />

            <div className="flex gap-x-2 min-h-6 items-center">
              <Title level={5} className="!w-[200px]  !m-0">
                {t("packages")}
              </Title>

              {adaptedPackages?.length ? (
                <>
                  <FormSelect
                    value={values?.packaging_ids}
                    placeholder={t("packages")}
                    fieldName="packaging_ids"
                    options={adaptedPackages}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                    showSearch
                    size="small"
                    className="!m-0"
                    multiple
                  />
                  <Button onClick={selectAllPackages}>{t("selectAll")}</Button>
                </>
              ) : (
                <Alert type="warning" message={t("noOptions")} />
              )}
            </div>
          </>
        )}
      </div>
      <Divider className="my-1.5" style={{ background: Colors.blue.primary }} />
    </>
  );
};

export { MultiplePackagingFilter };
